/**
 * Checks if the current time is outside a recurring time window.
 * Times are stored as UTC HH:MM:SS format.
 *
 * @param {Date} now - The current date/time
 * @param {string} startTimeStr - Start time in "HH:MM:SS" format (UTC)
 * @param {string} endTimeStr - End time in "HH:MM:SS" format (UTC)
 * @returns {boolean} - Returns true if outside the time window (should skip), false if within
 */
export const isOutsideTimeWindow = (now, startTimeStr, endTimeStr) => {
	const [ startHours, startMinutes, startSeconds, ] = (startTimeStr || '00:00:00').split(':').map(Number);
	const [ endHours, endMinutes, endSeconds, ] = (endTimeStr || '23:59:59').split(':').map(Number);

	// Create Date objects with today's UTC date and the UTC times
	const todayStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), startHours, startMinutes, startSeconds || 0, 0));
	const todayEnd = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), endHours, endMinutes, endSeconds || 0, 999));

	// Check if the time window wraps across midnight UTC
	const wrapsToNextDay = todayEnd <= todayStart;

	if (wrapsToNextDay) {
		// For wrap-around windows (e.g., 16:00 UTC to 00:30 UTC next day),
		// we need to check two possible windows:
		// 1. Yesterday's start to today's end (for the early morning hours)
		// 2. Today's start to tomorrow's end (for the evening hours)

		const yesterdayStart = new Date(todayStart.getTime() - 24 * 60 * 60 * 1000);
		const tomorrowEnd = new Date(todayEnd.getTime() + 24 * 60 * 60 * 1000);

		// Check if we're in yesterday's window (still ongoing) OR today's window
		const inYesterdayWindow = now >= yesterdayStart && now <= todayEnd;
		const inTodayWindow = now >= todayStart && now <= tomorrowEnd;

		return !inYesterdayWindow && !inTodayWindow;
	}

	// Non-wrapping window: simple check
	return now < todayStart || now > todayEnd;
};

/**
 * Determines if a per-file folder schedule is currently active.
 * Returns true (show the file) when:
 *  - folder_file_active is true (or not set — default active), AND
 *  - the current date falls within at least one of the folder_file_date_ranges windows
 *    (or no date ranges are configured, meaning no date restriction).
 *
 * @param {Object} slide - A folder-expanded slide with optional folder_file_active / folder_file_date_ranges
 * @returns {boolean} - false if the file should be suppressed, true otherwise
 */
export const isFolderFileActive = (slide) => {
	// If the file has been explicitly deactivated, suppress it.
	if (slide.folder_file_active === false) {
		return false;
	}

	const dateRanges = slide.folder_file_date_ranges;
	if (!Array.isArray(dateRanges) || dateRanges.length === 0) {
		// No date restrictions — always show.
		return true;
	}

	const today = new Date();
	// Compare against local midnight boundaries (YYYY-MM-DD strings from the API)
	const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

	return dateRanges.some(({ start_date, end_date }) => {
		if (start_date && todayStr < start_date) {
			return false;
		}
		if (end_date && todayStr > end_date) {
			return false;
		}
		return true;
	});
};

/**
 * Determines if a slide should be skipped based on its scheduling configuration.
 * Handles date/time ranges, day-of-week filtering, and recurring times with UTC conversion.
 *
 * @param {Object} slide - The slide object with optional scheduling configuration
 * @returns {boolean} - Returns true if the slide should be skipped, false if it should be shown
 */
export const shouldSkipSlide = (slide) => {
	const now = new Date();
	let scheduling = slide.scheduling;
	let scheduled = slide.scheduled;

	if (Array.isArray(slide.folder_slides)) {
		if (slide.folder_slides.length === 0) {
			return true; // skip empty folders
		}

		// folders share scheduling for all slides; the folder parent itself
		// carries no toggle, so read it from the same place as the rules
		scheduling = slide.folder_slides[0].scheduling;
		scheduled = slide.folder_slides[0].scheduled;
	}

	// scheduled === false is the deck row's scheduling toggle turned OFF —
	// always show, regardless of stale rules left in the scheduling JSON
	if (scheduled === false) {
		return false;
	}

	if (!scheduling) {
		return false;
	}

	// Check date range (start_date and end_date are in UTC ISO format)
	if (scheduling.start_date) {
		const startDate = new Date(scheduling.start_date);
		if (now < startDate) {
			return true; // Skip if before start date
		}
	}

	if (scheduling.end_date) {
		const endDate = new Date(scheduling.end_date);
		if (now > endDate) {
			return true; // Skip if after end date
		}
	}

	// Check recurring times (recurring_start_time and recurring_end_time are stored as UTC HH:MM:SS format)
	if (scheduling.recurring_start_time || scheduling.recurring_end_time) {
		if (isOutsideTimeWindow(now, scheduling.recurring_start_time, scheduling.recurring_end_time)) {
			return true; // Skip if outside the time window
		}
	}

	// Check day of week
	if (scheduling.days && Array.isArray(scheduling.days) && scheduling.days.length > 0) {
		const today = now.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
		if (!scheduling.days.includes(today)) {
			return true; // Skip if today is not in the allowed days
		}
	}

	// Check recurring times by day
	if (scheduling.recurring_times_by_day && Object.keys(scheduling.recurring_times_by_day).length > 0) {
		const today = now.getDay();
		// Keys from JSON are strings, so check both number and string keys
		const todaySchedule = scheduling.recurring_times_by_day[today] || scheduling.recurring_times_by_day[String(today)];

		// If today is not in the schedule, skip
		if (!todaySchedule) {
			return true;
		}

		// If use_recurring_times is false, the slide shows all day for this day (no time check needed)
		if (todaySchedule.use_recurring_times === false) {
			return false; // Don't skip - show all day
		}

		// If use_recurring_times is true, check the time window
		if (todaySchedule.use_recurring_times && (todaySchedule.start_time || todaySchedule.end_time)) {
			if (isOutsideTimeWindow(now, todaySchedule.start_time, todaySchedule.end_time)) {
				return true; // Skip if outside the time window
			}
		}
	}

	return false;
};

/**
 * Limits slides to fit within a slot budget, accounting for folders.
 * Regular slides count as 1 slot each. Folders count as their folder_slots value.
 * Rules-skipped regular slides don't count toward slots; slides whose
 * scheduling toggle is OFF (scheduled === false) always show, so they stay
 * in the deck and do count.
 * All folder slides are included (windowing handled at runtime by skipper).
 *
 * If a folder's configured slots exceed remaining capacity, the folder is
 * dynamically reduced to fit the available slots (minimum 1). This also
 * updates the returned folderLimits so windowing respects the reduced size.
 *
 * @param {Array} slides - Array of slide objects (after flatMap processing)
 * @param {number} slots - Maximum number of slots to fill
 * @param {Object} folderLimits - Map of folder_id to folder_slots
 * @param {Function} shouldSkip - Function to check if a slide should be skipped (default: shouldSkipSlide)
 * @returns {{ slides: Array, folderLimits: Object }} - Limited slides and potentially adjusted folderLimits
 */
export const limitSlidesToSlots = (slides, slots, folderLimits = {}, shouldSkip = shouldSkipSlide) => {
	if (!slots || slots >= slides.length) {
		return { slides, folderLimits };
	}

	let slotsUsed = 0;
	const seenFolders = new Set();
	const result = [];
	const adjustedFolderLimits = { ...folderLimits };

	for (const slide of slides) {
		if (slide.folder_id) {
			// Folders count as folder_slots toward the total
			// Include all folder slides - windowing handled at runtime
			if (!seenFolders.has(slide.folder_id)) {
				const folderSlotCost = folderLimits[slide.folder_id] || 1;
				const remainingSlots = slots - slotsUsed;

				if (remainingSlots < 1) {
					// No slots remaining, can't fit folder at all
					break;
				}

				if (folderSlotCost > remainingSlots) {
					// Reduce folder to fit available slots
					adjustedFolderLimits[slide.folder_id] = remainingSlots;
					slotsUsed += remainingSlots;
				} else {
					// Folder fits at full capacity
					slotsUsed += folderSlotCost;
				}

				seenFolders.add(slide.folder_id);
			}

			result.push(slide);
		} else {
			// Regular slides: check scheduling skip
			if (shouldSkip(slide)) {
				continue;
			}

			// Regular slides count as 1 slot
			if (slotsUsed + 1 > slots) {
				break;
			}

			slotsUsed++;
			result.push(slide);
		}
	}

	return { slides: result, folderLimits: adjustedFolderLimits };
};
